<template>
  <div class="add-keyword-selector">
    <div v-if="search" class="add-keyword-selector__search">
      <input
        ref="searchInput"
        v-model="query"
        class="text-input"
        :placeholder="searchPlaceholder"
        type="text"
        @click.stop
      />
    </div>

    <div v-if="type === 'tags'" class="add-keyword-selector__tags" @click.stop>
      <h5 class="add-keyword-selector__title" v-html="'Select an existing tag or create a new tag'" />

      <div class="add-keyword-selector__created">
        Create
        <div v-for="(createdTag, i) in tags" :key="i" class="add-keyword-selector__tag" v-html="createdTag.text" />
      </div>

      <vue-tags-input v-model="tag" :tags="tags" @tags-changed="updateTags" />
    </div>

    <ul>
      <span v-if="type === 'region'" class="add-keyword-selector__list-name" v-html="'Common regions'" />
      <li v-for="(item, i) in filteredItems" :key="i" :class="itemClasses(item)" @mousedown.stop="clickHandler(item)">
        <toggler v-if="type === 'options'" :toggler="item.toggler" @toggler_click="toggleOption(item)" />

        <span v-if="item.icon" :class="item.class" />

        <a :class="itemNameClasses" v-html="item.name" />

        <a
          v-if="item.question"
          class="add-keyword-selector__item-question"
          :href="item.question"
          target="_blank"
          @mousedown.stop
          v-html="'?'"
        />

        <span v-if="item.extra" class="add-keyword-selector__item-extra" v-html="item.extra" />

        <span v-if="item.description" :class="itemDescriptionClasses" v-html="item.description" />

        <template v-if="showLocationInputs(item)">
          <label class="text-label" :for="`option-${i}`">
            {{ item.inputLabel }}
          </label>
          <custom-google-autocomplete
            :id="`option-${i}`"
            :ref="item.name === 'Change Location' ? 'keywordLocation' : 'keywordGMB'"
            class="text-input"
            :placeholder="item.inputPlaceholder"
            :types="geotype"
            @keypress="zUp()"
            @inputChange="
              ($event) =>
                emptyInputAutocompleteHandler(
                  $event.newVal,
                  item.name === 'Change Location' ? 'location' : 'GMB',
                  'fromDropMenu',
                )
            "
            @placechanged="(data, place, id) => updateTutorialLocationWeb(data, place, id, item)"
          />
        </template>

        <span class="kw kw-check" />
      </li>
    </ul>
  </div>
</template>

<script>
import AddKeywordsMixin from '@/mixins/AddKeywordsMixin';
import GoogleApiMixin from '@/mixins/GoogleApiMixin';
const CustomGoogleAutocomplete = () => import('@/components/ui-elements/CustomGoogleAutocomplete');
const VueTagsInput = () => import('@johmun/vue-tags-input');
export default {
  name: 'AddKeywordSelector',
  components: {
    'custom-google-autocomplete': CustomGoogleAutocomplete,
    'vue-tags-input': VueTagsInput,
  },
  mixins: [GoogleApiMixin, AddKeywordsMixin],
  props: {
    closeHandler: {
      type: Function,
      required: true,
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    type: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
    valueObject: {
      type: Object,
      required: false,
      default: () => {},
    },
    search: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      query: '',
      tag: '',
      tags: [],
    };
  },
  computed: {
    filteredItems() {
      return this.search && this.query.length && this.type !== 'options'
        ? this.items.filter((el) => {
            const exp = new RegExp(`^.*${this.query.toLowerCase()}.*$`, 'i');
            return exp.test(el.name.toLowerCase());
          })
        : this.items;
    },
    // computed classes
    itemNameClasses() {
      return {
        'add-keyword-selector__item-name': true,
        'add-keyword-selector__item-name_options': this.type === 'options',
      };
    },
    itemDescriptionClasses() {
      return {
        'add-keyword-selector__item-description': true,
        'add-keyword-selector__item-description_options': this.type === 'options',
      };
    },
  },
  created() {
    if (this.type === 'tags' && this.value) {
      this.tags = this.value.split(',').map((el) => ({ text: el, tiClasses: ['ti-valid'] }));
    }
  },
  beforeMount() {
    if (this.type === 'options' && this.valueObject.GMB) {
      this.items[0].toggler = false;
    }

    if (this.type === 'options' && this.valueObject.location) {
      this.items[1].toggler = true;
    }
  },
  mounted() {
    if (this.$refs.keywordGMB) {
      this.$refs.keywordGMB[0].autocompleteText = this.valueObject.GMB;
    }

    if (this.$refs.keywordLocation) {
      this.$refs.keywordLocation[0].autocompleteText = this.valueObject.location;
    }

    if (this.search) {
      this.$refs.searchInput.focus();
    }
  },
  methods: {
    clickHandler(item) {
      if (this.type === 'options') return;

      this.closeHandler(this.type);
      this.$emit('item-click', { type: this.type, item });
    },
    toggleOption(item) {
      item.toggler = !item.toggler;
      const options = { ...this.valueObject };

      if (item.name === 'Change Location') {
        options.changeLocation = item.toggler;
        options.location = '';
      } else {
        options.ignoreLocal = item.toggler;
        options.GMB = '';
      }

      this.$emit('update:valueObject', options);
    },
    showLocationInputs(item) {
      let show = false;

      if (this.type === 'options') {
        show = item.name === 'Change Location' ? item.toggler : !item.toggler;
      }

      return show;
    },
    updateTags(newTags) {
      this.tags = newTags;
      this.$emit(
        'update:value',
        // tags texts joined by ','
        newTags.reduce((acc, curr) => (acc.length ? (acc += `,${curr.text}`) : (acc += curr.text)), ''),
      );
    },
    // methods for classes
    itemClasses(item) {
      const value = this.type === 'domain' ? item.name : item.value;

      return {
        options: this.type === 'options',
        platform: this.type === 'platform',
        active: this.type !== 'options' && this.value === value,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~sass/partials/tutorial';
@import '~sass/partials/inputs';

.add-keyword-selector {
  background: kw-color(kw-white, 1);
  border: 1px solid #dee5ec;
  border-radius: 4px;
  box-shadow: 0 6px 8px rgba(18, 27, 41, 0.1);
  max-height: 316px;
  position: absolute;
  z-index: 1;

  &:before {
    background: kw-color(kw-white, 1);
    border-top-left-radius: 4px;
    content: '';
    height: 9px;
    position: absolute;
    transform: rotate(45deg);
    width: 9px;
    z-index: 1;
  }
}

.add-keyword-selector_domain {
  @extend .menu_bottom-right;
  width: 100%;
}

.add-keyword-selector_region,
.add-keyword-selector_language {
  @extend .menu_bottom-left;
  width: 266px;
}

.add-keyword-selector_options {
  @extend .menu_bottom-left;
  width: 310px;
}

.add-keyword-selector_platform {
  @extend .menu_top-left;
  left: 20px;
  width: 206px;
  @media screen and (max-width: 959px) {
    max-width: 110%;
  }
}

.add-keyword-selector_billing,
.add-keyword-selector_updates {
  @extend .menu_bottom-left;
  left: 8px;
  width: calc(100% - 16px);
}

.add-keyword-selector_tags {
  @extend .menu_top-left;
  left: 20px;
  width: 310px;
}

.add-keyword-selector__search {
  padding: 10px;

  input {
    width: 100%;
  }
}

.add-keyword-selector__tags {
  padding: 10px;

  ::v-deep .ti-input {
    border: 1px solid #dee5ec;
    border-radius: 4px;

    .ti-tag {
      @extend .custom-tag;
    }

    input {
      width: 100%;
    }
  }

  ::v-deep .ti-tags {
    @extend .tutorial-scroll;
    max-height: 100px;
    overflow-y: auto;
  }
}

.add-keyword-selector__tag {
  @extend .custom-tag;
  margin: 2px;
  padding: 3px 5px;

  &:first-child {
    margin-left: 4px;
  }
}

.add-keyword-selector__title {
  color: #868686;
  font-size: 13px;
  font-weight: normal;
  line-height: 20px;
  margin: 0;
}

.add-keyword-selector__created {
  background: rgba(222, 229, 236, 0.35);
  display: flex;
  flex-flow: row wrap;
  line-height: 24px;
  margin: 0 -10px 8px;
  padding: 4px 10px;
}

.add-keyword-selector__list-name {
  color: #868686;
  font-size: 11px;
  line-height: 20px;
  padding-left: 12px;
}

ul {
  @extend .tutorial-scroll;
  margin: 0;
  margin-right: 2px;
  max-height: 260px;
  overflow: auto;
  padding: 0;
  list-style-type: none;
}

li {
  border-bottom: 1px solid #dee5ec;
  cursor: pointer;
  padding: 12px 12px 10px 12px;
  position: relative;
  display: block;

  .toggler {
    left: unset !important;
  }

  &.options {
    padding-right: 12px;
  }

  .icon-desktop-mobile {
    font-size: 18px;
  }

  span.kw-check {
    color: transparent;
    font-size: 10px;
    font-weight: 700;
    position: absolute;
    right: 11px;
    top: 17px;
  }

  &.active {
    span.kw-check {
      color: #4084f6;
    }

    &.platform {
      .add-keyword-selector__item-name {
        color: kw-color(kw-blue, 1);
      }
    }
  }

  ::v-deep .add-keyword__flag {
    margin-bottom: -2px;
    margin-right: 8px;
  }

  label {
    padding-top: 4px;
  }

  input {
    width: 100%;
  }

  &:last-child {
    border-bottom: none;
  }
}

.add-keyword-selector__item-name {
  @import '~sass/cutting-name';
  color: kw-color(kw-black, 1);
  font-size: 13px;
  line-height: 20px;
  text-decoration: none;
}

.add-keyword-selector__item-name_options {
  padding-left: 54px;
}

.add-keyword-selector__item-icon {
  height: 18px;
  margin-bottom: 2px;
  margin-right: 8px;
  width: 18px;
}

.add-keyword-selector__item-description {
  @import '~sass/cutting-name';
  color: #9d9fa5;
  display: block;
  font-size: 11px;
  line-height: 13px;
}

.add-keyword-selector__item-description_options {
  padding-left: 54px;
}

.add-keyword-selector__item-question {
  @extend .tutorial-question;
}

.add-keyword-selector__item-extra {
  margin-left: 24px;
}
</style>
